/**
 * ConciergeMessagePart — renders message parts from the Mastra agent stream.
 *
 * Mastra stores tool interactions as separate `tool-call` / `tool-result` parts.
 * The renderers expect a merged `tool-{toolName}` shape with `state`, `input`, and
 * `output` fields. `normalizeParts` bridges the two formats.
 */
import React, { useMemo } from 'react';
import { ConciergeMessagePartProps, MessagePart } from './types';
import { ConciergePartItem } from './ConciergePartItem';

/**
 * Merge native Mastra `tool-call` / `tool-result` pairs into the
 * `tool-{toolName}` shape that renderers consume, preserving all other parts.
 */
function normalizeParts(raw: MessagePart[]): MessagePart[] {
  const resultByCallId = new Map<string, any>();
  for (const p of raw) {
    if ((p as any).type === 'tool-result') {
      resultByCallId.set((p as any).toolCallId, p);
    }
  }

  const consumedResultIds = new Set<string>();
  const out: MessagePart[] = [];

  for (const p of raw) {
    const part = p as any;

    if (part.type === 'tool-call') {
      const matching = resultByCallId.get(part.toolCallId);
      const merged: any = {
        type: `tool-${part.toolName}`,
        toolCallId: part.toolCallId,
        state: matching ? 'result' : 'call',
        input: part.args,
      };
      if (matching) {
        merged.output = matching.result;
        consumedResultIds.add(part.toolCallId);
      }
      out.push(merged);
    } else if (part.type === 'tool-result') {
      if (!consumedResultIds.has(part.toolCallId)) {
        out.push({
          type: `tool-${part.toolName}`,
          toolCallId: part.toolCallId,
          state: 'result',
          output: part.result,
        } as any);
      }
    } else {
      out.push(p);
    }
  }
  return out;
}

export function ConciergeMessagePart({
  parts,
  onProductClick,
  isStreaming,
  pdpStacklineSku,
  isOnChatWidget,
  apiBaseUrl,
}: ConciergeMessagePartProps) {
  if (!parts || parts.length === 0) return null;
  const normalizedParts = useMemo(() => normalizeParts(parts as MessagePart[]), [parts]);

  return (
    <>
      {normalizedParts.map((part, index) => (
        <ConciergePartItem
          key={`${part.type}-${index}`}
          part={part}
          allParts={normalizedParts}
          onProductClick={onProductClick}
          isStreaming={isStreaming}
          pdpStacklineSku={pdpStacklineSku}
          isOnChatWidget={isOnChatWidget}
          apiBaseUrl={apiBaseUrl}
        />
      ))}
    </>
  );
}
